import { Component, ChangeDetectionStrategy, OnInit } from '@angular/core'
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatIcon } from '@angular/material/icon'
import { Title } from '@angular/platform-browser'
import { Router } from '@angular/router'
import { lastValueFrom } from 'rxjs'

import { LoadingComponent } from '../../components/loading/loading.component'
import { ButtonComponent } from '../../components/button/button.component'
import { AuthService } from '../../services/auth.service'
import { MfaService } from '../../services/mfa.service'
import { getTitle } from '../../util/accessibility'
import { invalidStringValidator } from '../../directives/validator/custom-validator'

const VERIFICATION_CODE_REGEX = /^\d{6}$/

@Component({
  selector: 'app-totp-verification-page',
  standalone: true,
  imports: [LoadingComponent, ButtonComponent, MatIcon, ReactiveFormsModule],
  templateUrl: './totp-verification-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './totp-verification-page.component.scss',
})
export class TotpVerificationPageComponent implements OnInit {
  isLoading = false
  verificationCode = new FormControl('', {
    nonNullable: true,
    validators: [
      Validators.required,
      invalidStringValidator(VERIFICATION_CODE_REGEX),
    ],
  })

  totpVerificationForm = new FormGroup({
    verificationCode: this.verificationCode,
  })

  constructor(
    private router: Router,
    private authService: AuthService,
    private mfaService: MfaService,
    private titleService: Title,
  ) {
    this.titleService.setTitle(getTitle('Authenticator App認証'))
  }

  /**
   * TOTP MFA 検証に必要なログイン状態か確認する。
   */
  ngOnInit(): void {
    const loginResult = this.authService.getLoginResult()
    if (
      !loginResult?.resolver ||
      !loginResult.factorUid ||
      !loginResult.resolver.hints.some(
        (hint) =>
          hint.uid === loginResult.factorUid && hint.factorId === 'totp',
      )
    ) {
      this.router.navigate(['/login'])
    }
  }

  /**
   * 複数の要素がある場合は選択画面、それ以外はログイン画面へ戻る。
   */
  async backToLogin(): Promise<void> {
    const resolver = this.authService.getLoginResult()?.resolver
    if (resolver && resolver.hints.length >= 2) {
      await this.router.navigate(['/auth/mfa-verify-select'])
      return
    }
    await this.authService.signOutPreservingReturnUrl()
    this.authService.redirectToLogin()
  }

  /**
   * 入力された TOTP コードでサインインを完了する。
   */
  sendVerificationCodeSubmit = async (event: Event): Promise<void> => {
    event.preventDefault()

    if (this.isLoading || this.totpVerificationForm.invalid) {
      return
    }

    const loginResult = this.authService.getLoginResult()

    if (!loginResult || !loginResult.resolver || !loginResult.factorUid) {
      this.router.navigate(['/login'])
      return
    }

    this.isLoading = true
    try {
      await lastValueFrom(
        this.mfaService.verifyTotpCode(
          loginResult.resolver,
          loginResult.factorUid,
          this.verificationCode.value,
        ),
      )
    } catch (error) {
      let errorMessage = 'エラーが発生しました。'
      const code = (error as { code?: string }).code
      if (code === 'auth/invalid-verification-code') {
        errorMessage = '認証コードが無効です。'
      } else if (code === 'auth/too-many-requests') {
        errorMessage =
          '試行回数が多すぎます。しばらく待ってから再度お試しください。'
      } else if (
        code === 'auth/invalid-multi-factor-session' ||
        code === 'auth/missing-multi-factor-session' ||
        code === 'auth/code-expired'
      ) {
        alert(
          '認証セッションの有効期限が切れました。ログインからやり直してください。',
        )
        this.isLoading = false
        await this.authService.signOutPreservingReturnUrl()
        this.authService.redirectToLogin()
        return
      }
      alert(errorMessage)
      this.isLoading = false
      return
    }

    const returnUrl = this.authService.consumeReturnUrl() || '/home'
    this.authService.clearLoginState()
    await this.router.navigateByUrl(returnUrl)
  }
}
